<template>
  <div class="container py-5">
    <div class="text-center mb-5">
      <h1 class="display-4 fw-bold">Digital Skills Training</h1>
      <p class="lead">Empowering seniors with essential digital literacy skills for modern life.</p>
    </div>

    <div class="row g-5 align-items-center mb-5">
      <div class="col-md-6 order-md-2">
        <div class="position-relative">
          <img
            src="../assets/images/Snipaste_2025-07-23_11-37-40.png"
            class="img-fluid rounded-4 shadow-sm"
            alt="Digital Skills Training"
            style="width: 100%; max-width: 600px"
          />
          <div class="position-absolute top-0 end-0 p-3">
            <span class="badge bg-primary">New</span>
          </div>
        </div>
      </div>

      <div class="col-md-6 order-md-1">
        <h2 class="fw-bold mb-4">Master Essential Digital Skills</h2>
        <p class="mb-4">
          In today's digital age, proficiency with technology is essential for maintaining
          independence and staying connected. Our comprehensive training program is specifically
          designed to help seniors build confidence and competence with modern digital tools.
        </p>

        <div class="d-flex align-items-start gap-3 mb-3">
          <i class="bi bi-check-circle-fill fs-4 text-primary"></i>
          <div>
            <h5 class="fw-bold">Personalized Instruction</h5>
            <p class="mb-0">
              One-on-one and small group sessions tailored to individual learning paces.
            </p>
          </div>
        </div>

        <div class="d-flex align-items-start gap-3 mb-3">
          <i class="bi bi-check-circle-fill fs-4 text-primary"></i>
          <div>
            <h5 class="fw-bold">Practical Curriculum</h5>
            <p class="mb-0">
              Focus on real-world applications that enhance daily life and communication.
            </p>
          </div>
        </div>

        <div class="d-flex align-items-start gap-3 mb-3">
          <i class="bi bi-check-circle-fill fs-4 text-primary"></i>
          <div>
            <h5 class="fw-bold">Ongoing Support</h5>
            <p class="mb-0">Continuous assistance and refresher sessions to reinforce learning.</p>
          </div>
        </div>

        <div class="mt-4">
          <a href="/register" class="btn btn-primary btn-lg">Register for Training</a>
        </div>
      </div>
    </div>

    <div class="mt-5 pt-5">
      <h2 class="text-center fw-bold mb-5">Course Curriculum</h2>
      <div class="row g-4">
        <div class="col-md-6 col-lg-4">
          <div class="card h-100 border-0 shadow-sm hover-lift">
            <div class="card-body d-flex flex-column p-4">
              <div
                class="d-flex align-items-center justify-content-center w-50px h-50px rounded-circle bg-primary bg-opacity-10 mb-3"
              >
                <i class="bi bi-phone fs-3 text-primary"></i>
              </div>
              <h5 class="card-title fw-bold">Smartphone Basics</h5>
              <p class="card-text mb-3">
                Learn to navigate your device, use apps, and stay connected with family and friends.
              </p>
              <ul class="list-check mt-auto">
                <li><i class="bi bi-check2-circle text-success me-2"></i>Device navigation</li>
                <li><i class="bi bi-check2-circle text-success me-2"></i>Contact management</li>
                <li><i class="bi bi-check2-circle text-success me-2"></i>Camera usage</li>
                <li><i class="bi bi-check2-circle text-success me-2"></i>App installation</li>
              </ul>
            </div>
          </div>
        </div>
        <div class="col-md-6 col-lg-4">
          <div class="card h-100 border-0 shadow-sm hover-lift">
            <div class="card-body d-flex flex-column p-4">
              <div
                class="d-flex align-items-center justify-content-center w-50px h-50px rounded-circle bg-primary bg-opacity-10 mb-3"
              >
                <i class="bi bi-laptop fs-3 text-primary"></i>
              </div>
              <h5 class="card-title fw-bold">Computer Fundamentals</h5>
              <p class="card-text mb-3">
                Master essential computer skills for everyday use and online engagement.
              </p>
              <ul class="list-check mt-auto">
                <li><i class="bi bi-check2-circle text-success me-2"></i>Internet browsing</li>
                <li><i class="bi bi-check2-circle text-success me-2"></i>Email communication</li>
                <li><i class="bi bi-check2-circle text-success me-2"></i>Document creation</li>
                <li><i class="bi bi-check2-circle text-success me-2"></i>File management</li>
              </ul>
            </div>
          </div>
        </div>
        <div class="col-md-6 col-lg-4">
          <div class="card h-100 border-0 shadow-sm hover-lift">
            <div class="card-body d-flex flex-column p-4">
              <div
                class="d-flex align-items-center justify-content-center w-50px h-50px rounded-circle bg-primary bg-opacity-10 mb-3"
              >
                <i class="bi bi-wifi fs-3 text-primary"></i>
              </div>
              <h5 class="card-title fw-bold">Online Safety</h5>
              <p class="card-text mb-3">
                Protect yourself from digital threats and maintain privacy online.
              </p>
              <ul class="list-check mt-auto">
                <li><i class="bi bi-check2-circle text-success me-2"></i>Password security</li>
                <li><i class="bi bi-check2-circle text-success me-2"></i>Phishing prevention</li>
                <li><i class="bi bi-check2-circle text-success me-2"></i>Secure browsing</li>
                <li><i class="bi bi-check2-circle text-success me-2"></i>Privacy settings</li>
              </ul>
            </div>
          </div>
        </div>
        <div class="col-md-6 col-lg-4">
          <div class="card h-100 border-0 shadow-sm hover-lift">
            <div class="card-body d-flex flex-column p-4">
              <div
                class="d-flex align-items-center justify-content-center w-50px h-50px rounded-circle bg-primary bg-opacity-10 mb-3"
              >
                <i class="bi bi-shop fs-3 text-primary"></i>
              </div>
              <h5 class="card-title fw-bold">Online Shopping</h5>
              <p class="card-text mb-3">
                Learn to shop safely and confidently online with step-by-step guidance.
              </p>
              <ul class="list-check mt-auto">
                <li><i class="bi bi-check2-circle text-success me-2"></i>E-commerce platforms</li>
                <li><i class="bi bi-check2-circle text-success me-2"></i>Payment methods</li>
                <li><i class="bi bi-check2-circle text-success me-2"></i>Order tracking</li>
                <li><i class="bi bi-check2-circle text-success me-2"></i>Return policies</li>
              </ul>
            </div>
          </div>
        </div>
        <div class="col-md-6 col-lg-4">
          <div class="card h-100 border-0 shadow-sm hover-lift">
            <div class="card-body d-flex flex-column p-4">
              <div
                class="d-flex align-items-center justify-content-center w-50px h-50px rounded-circle bg-primary bg-opacity-10 mb-3"
              >
                <i class="bi bi-chat-dots fs-3 text-primary"></i>
              </div>
              <h5 class="card-title fw-bold">Social Connectivity</h5>
              <p class="card-text mb-3">
                Stay connected with loved ones through digital communication tools.
              </p>
              <ul class="list-check mt-auto">
                <li><i class="bi bi-check2-circle text-success me-2"></i>Video calling</li>
                <li><i class="bi bi-check2-circle text-success me-2"></i>Social media</li>
                <li><i class="bi bi-check2-circle text-success me-2"></i>Messaging apps</li>
                <li><i class="bi bi-check2-circle text-success me-2"></i>Email etiquette</li>
              </ul>
            </div>
          </div>
        </div>
        <div class="col-md-6 col-lg-4">
          <div class="card h-100 border-0 shadow-sm hover-lift">
            <div class="card-body d-flex flex-column p-4">
              <div
                class="d-flex align-items-center justify-content-center w-50px h-50px rounded-circle bg-primary bg-opacity-10 mb-3"
              >
                <i class="bi bi-bank fs-3 text-primary"></i>
              </div>
              <h5 class="card-title fw-bold">Online Banking</h5>
              <p class="card-text mb-3">
                Manage your finances securely and conveniently through digital banking platforms.
              </p>
              <ul class="list-check mt-auto">
                <li><i class="bi bi-check2-circle text-success me-2"></i>Account management</li>
                <li><i class="bi bi-check2-circle text-success me-2"></i>Funds transfer</li>
                <li><i class="bi bi-check2-circle text-success me-2"></i>Bill payment</li>
                <li><i class="bi bi-check2-circle text-success me-2"></i>Fraud detection</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-5 pt-5">
      <h2 class="text-center fw-bold mb-5">Training Formats</h2>
      <div class="row g-4">
        <div class="col-md-6">
          <div class="card border-0 shadow-sm h-100">
            <div class="card-body p-4 d-flex flex-column">
              <h3 class="h5 fw-bold mb-3 d-flex align-items-center gap-2">
                <i class="bi bi-person-video3 fs-3 text-primary"></i> In-Person Training
              </h3>
              <p>
                Face-to-face instruction at our community centers with hands-on guidance and
                immediate feedback.
              </p>
              <ul class="list-check mt-auto">
                <li><i class="bi bi-check2-circle text-success me-2"></i>Personalized attention</li>
                <li><i class="bi bi-check2-circle text-success me-2"></i>Interactive learning</li>
                <li><i class="bi bi-check2-circle text-success me-2"></i>Peer interaction</li>
              </ul>
              <div class="mt-3">
                <a href="/contact" class="btn btn-outline-primary">Find Local Classes</a>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="card border-0 shadow-sm h-100">
            <div class="card-body p-4 d-flex flex-column">
              <h3 class="h5 fw-bold mb-3 d-flex align-items-center gap-2">
                <i class="bi bi-cloud-arrow-down fs-3 text-primary"></i> Online Learning
              </h3>
              <p>
                Self-paced digital courses with video tutorials and interactive exercises accessible
                from home.
              </p>
              <ul class="list-check mt-auto">
                <li><i class="bi bi-check2-circle text-success me-2"></i>24/7 access</li>
                <li><i class="bi bi-check2-circle text-success me-2"></i>Progress tracking</li>
                <li><i class="bi bi-check2-circle text-success me-2"></i>Support resources</li>
              </ul>
              <div class="mt-3">
                <a href="/digital-resources" class="btn btn-outline-primary"
                  >Access Online Courses</a
                >
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.hover-lift {
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease;
}
.hover-lift:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}

.list-check li {
  margin-bottom: 0.5rem;
}
</style>
